import { InventoryHealthDiagnostic } from '../../types/inventoryHealth'
import { Option } from './SelectionFilters'

/**
 * The channels offered by the channel picker.
 *
 * Derived from the diagnostic response rather than a separate query, so the
 * picker offers exactly the channels this report covered. A list sourced
 * elsewhere could offer a channel the report has no rows for, which renders as
 * "no data" when it is really "wrong filter" — the same confusion this screen
 * exists to end.
 *
 * Credentials are read FIRST because they list every MAPPED channel, including
 * one holding no inventory at all. Deriving the list from the rows alone would
 * omit exactly the channel most worth selecting: the one that is broken.
 */
export function deriveChannelOptions(
  result: InventoryHealthDiagnostic | undefined | null
): Option[] {
  if (result == null) {
    return []
  }

  const byID = new Map<string, string>()

  result.credentials.forEach((providerHealth) =>
    providerHealth.channels.forEach((channel) => {
      // The CODE is preferred over the id. Subscriptions key on the channel
      // code, and the code is populated on every mapping while the id is not —
      // eight of fifteen were empty on the property that motivated this screen.
      // The server accepts either, so preferring the code simply picks the
      // identifier the data model actually uses.
      const value = channel.channelCode || channel.channelID
      if (value !== '') {
        byID.set(value, channel.channelName || channel.channelCode || value)
      }
    })
  )

  // Rows fill gaps rather than overwrite: a channel present in both should keep
  // the credential panel's name, which is the mapping's own label.
  result.rows.forEach((row) => {
    if (row.channelID !== '' && !byID.has(row.channelID)) {
      byID.set(row.channelID, row.channelName || row.channelID)
    }
  })

  return Array.from(byID.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}
